import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, Users, Calendar, CheckCircle2, Archive, ArrowRight, FileText } from 'lucide-react';
import { JobFilters } from './types';
import { completedJobs as mockCompletedJobs } from '../../../data/mockJobs';
import { jobsApi, Job } from '../../../api/jobs';
import JobDetailsModal from '../JobDetailsModal';
import FinalReportModal from './FinalReportModal';
import { useLanguage } from '../../../contexts/LanguageContext';

interface JobsListProps {
  filters: JobFilters;
}

interface DisplayJob {
  id: string;
  title: string;
  department: string;
  location: string;
  candidates: number;
  completedDate: string;
  status: string;
  priority?: string;
  referencesNeeded?: number;
  referencesCompleted?: number;
  dueDate?: string;
}

const JobsList = ({ filters }: JobsListProps) => {
  const [completedJobs, setCompletedJobs] = useState<DisplayJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<DisplayJob | null>(null);
  const [reportJob, setReportJob] = useState<DisplayJob | null>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const fetchCompletedJobs = async () => {
      try {
        setLoading(true);
        const allJobs = await jobsApi.getAll();
        
        // Filter jobs with status "COMPLETED", exclude "CANCELLED" jobs
        const completed = allJobs
          .filter(job => job.status === 'COMPLETED' && job.status !== 'CANCELLED')
          .map(job => ({
            id: job.id,
            title: job.job_title,
            department: job.department,
            location: 'Remote', // Using default since API doesn't have location
            candidates: 0, // Will be updated with actual candidate count
            completedDate: job.application_deadline, // Using deadline as completion date
            status: 'completed',
            priority: 'low', // Default priority for completed jobs
            referencesNeeded: 0,
            referencesCompleted: 0,
            dueDate: job.application_deadline
          }));
          
        // Get candidate count for each job
        for (const job of completed) {
          const candidates = await jobsApi.getCandidates(job.id);
          job.candidates = candidates.length;
          
          // Count completed references
          const completedRefs = candidates.reduce((total, candidate) => {
            return total + (candidate.references?.filter(ref => ref.filled_form_at)?.length || 0);
          }, 0);
          
          job.referencesCompleted = completedRefs;
          job.referencesNeeded = candidates.length * 3; // Assuming 3 references per candidate
        }
        
        setCompletedJobs(completed);
      } catch (error) {
        console.error('Error fetching completed jobs:', error);
        // Fallback to mock data if API fails
        setCompletedJobs(mockCompletedJobs.map(job => ({
          ...job,
          status: 'completed',
          priority: 'low',
          referencesNeeded: job.candidates * 3,
          referencesCompleted: job.referencesNeeded,
          dueDate: job.completedDate
        })));
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedJobs();
  }, []);

  const filteredJobs = completedJobs
    .filter(job => {
      const jobDate = new Date(job.completedDate);
      const matchesMonth = jobDate.getMonth() === filters.month;
      const matchesYear = jobDate.getFullYear() === filters.year;
      const matchesDepartment = !filters.department || job.department === filters.department;
      return matchesMonth && matchesYear && matchesDepartment;
    })
    // Sort by completion date (most recent first)
    .sort((a, b) => {
      const dateA = new Date(a.completedDate).getTime();
      const dateB = new Date(b.completedDate).getTime();
      return dateB - dateA;
    });

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center animate-pulse">
            <Archive className="w-6 h-6 text-gray-400" />
          </div>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">{t('jobs.loading')}</h3>
        <p className="text-gray-500">{t('common.loading')}</p>
      </div>
    );
  }
  
  if (filteredJobs.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
            <Archive className="w-6 h-6 text-gray-400" />
          </div>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">{t('jobs.empty.title')}</h3>
        <p className="text-gray-500">{t('jobs.empty.filtered')}</p>
      </div>
    );
  }

  const viewJobDetails = (job: DisplayJob) => {
    setSelectedJob(job);
  };
  
  const handleViewCandidates = () => {
    if (selectedJob) {
      navigate(`/candidates?jobId=${selectedJob.id}`);
      setSelectedJob(null);
    }
  };

  const openFinalReport = (job: DisplayJob, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent job card click
    setReportJob(job);
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredJobs.map((job) => (
          <div 
            key={job.id} 
            className="bg-white rounded-lg border border-purple-100 p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer"
            onClick={() => viewJobDetails(job)}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-semibold text-gray-900">{job.title}</h3>
              <div className="flex items-center gap-1 text-purple-600 bg-purple-50 px-2 py-1 rounded-full text-xs font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>{t('common.completed')}</span>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Building2 className="w-4 h-4" />
                <span>{job.department}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="w-4 h-4" />
                <span>{job.location}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users className="w-4 h-4" />
                <span>{job.candidates} {t('jobs.completedJob.candidates')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>{t('jobs.completedJob.completedOn', { date: new Date(job.completedDate).toLocaleDateString() })}</span>
              </div>
            </div>

            <div className="pt-4 border-t border-purple-100 space-y-2">
              <button
                className="w-full flex items-center justify-center gap-1 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 py-2 rounded-lg transition-colors"
                onClick={(e) => openFinalReport(job, e)}
              >
                <FileText className="w-3.5 h-3.5" /> {t('jobs.completedJob.seeFinalReport')}
              </button>
              
              <button
                className="w-full flex items-center justify-center gap-1 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 py-2 rounded-lg transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/candidates?jobId=${job.id}&status=COMPLETED`);
                }}
              >
                {t('common.viewDetails')} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedJob && (
        <JobDetailsModal 
          job={selectedJob as any} 
          onClose={() => setSelectedJob(null)}
        />
      )}

      {reportJob && (
        <FinalReportModal
          jobId={reportJob.id}
          jobTitle={reportJob.title}
          onClose={() => setReportJob(null)}
        />
      )}
    </>
  );
};

export default JobsList;